import {getCountries, getExampleNumber, parsePhoneNumberFromString, validatePhoneNumberLength} from 'libphonenumber-js/max';
import mobileExamples from 'libphonenumber-js/mobile/examples';

const supportedCountries = new Set(getCountries());
const countryStorageKey = 'quiz-country-v2';

// A browser language is a preference, not a reliable location. The timezone is
// a better local fallback when the GeoIP request is blocked or times out.
const timeZoneCountries = new Map([
  ['America/New_York', 'us'],
  ['America/Detroit', 'us'],
  ['America/Indiana/Indianapolis', 'us'],
  ['America/Indiana/Knox', 'us'],
  ['America/Indiana/Marengo', 'us'],
  ['America/Indiana/Petersburg', 'us'],
  ['America/Indiana/Tell_City', 'us'],
  ['America/Indiana/Vevay', 'us'],
  ['America/Indiana/Vincennes', 'us'],
  ['America/Indiana/Winamac', 'us'],
  ['America/Chicago', 'us'],
  ['America/Denver', 'us'],
  ['America/Phoenix', 'us'],
  ['America/Los_Angeles', 'us'],
  ['America/Anchorage', 'us'],
  ['America/Adak', 'us'],
  ['America/Juneau', 'us'],
  ['America/Metlakatla', 'us'],
  ['America/Nome', 'us'],
  ['America/Sitka', 'us'],
  ['America/Yakutat', 'us'],
  ['Pacific/Honolulu', 'us'],
  ['America/Bogota', 'co'],
  ['America/Managua', 'ni'],
  ['America/Guatemala', 'gt'],
  ['America/El_Salvador', 'sv'],
  ['America/Tegucigalpa', 'hn'],
  ['America/Costa_Rica', 'cr'],
  ['America/Panama', 'pa'],
  ['America/Mexico_City', 'mx'],
  ['America/Cancun', 'mx'],
  ['America/Merida', 'mx'],
  ['America/Monterrey', 'mx'],
  ['America/Matamoros', 'mx'],
  ['America/Chihuahua', 'mx'],
  ['America/Hermosillo', 'mx'],
  ['America/Mazatlan', 'mx'],
  ['America/Ojinaga', 'mx'],
  ['America/Tijuana', 'mx'],
  ['America/Bahia_Banderas', 'mx'],
  ['America/Caracas', 've'],
  ['America/Lima', 'pe'],
  ['America/Guayaquil', 'ec'],
  ['Pacific/Galapagos', 'ec'],
  ['America/La_Paz', 'bo'],
  ['America/Santiago', 'cl'],
  ['Pacific/Easter', 'cl'],
  ['America/Asuncion', 'py'],
  ['America/Argentina/Buenos_Aires', 'ar'],
  ['America/Argentina/Catamarca', 'ar'],
  ['America/Argentina/Cordoba', 'ar'],
  ['America/Argentina/Jujuy', 'ar'],
  ['America/Argentina/La_Rioja', 'ar'],
  ['America/Argentina/Mendoza', 'ar'],
  ['America/Argentina/Rio_Gallegos', 'ar'],
  ['America/Argentina/Salta', 'ar'],
  ['America/Argentina/San_Juan', 'ar'],
  ['America/Argentina/San_Luis', 'ar'],
  ['America/Argentina/Tucuman', 'ar'],
  ['America/Argentina/Ushuaia', 'ar'],
  ['America/Montevideo', 'uy'],
  ['America/Havana', 'cu'],
  ['America/Santo_Domingo', 'do'],
  ['America/Puerto_Rico', 'pr'],
  ['Europe/Madrid', 'es'],
  ['Europe/Ceuta', 'es'],
  ['Atlantic/Canary', 'es'],
  ['Africa/Malabo', 'gq']
]);

export function detectCountryFromTimeZone(timeZone) {
  const country = timeZoneCountries.get(timeZone);
  return country && supportedCountries.has(country.toUpperCase()) ? country : '';
}

function browserTimeZoneCountry() {
  if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return '';
  try {
    return detectCountryFromTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone ?? '');
  } catch {
    return '';
  }
}

function browserLocaleCountry() {
  if (typeof navigator === 'undefined' || typeof Intl === 'undefined' || typeof Intl.Locale !== 'function') return '';
  const locales = [navigator.language, ...(navigator.languages ?? [])].filter(Boolean);
  for (const locale of locales) {
    try {
      const parsedLocale = new Intl.Locale(locale);
      const region = parsedLocale.region?.toUpperCase();
      // `es-ES` is commonly the browser language of Spanish-speaking users,
      // including users who are physically in the US or Latin America.
      if (parsedLocale.language === 'es' && region === 'ES') continue;
      if (region && supportedCountries.has(region)) return region.toLowerCase();
    } catch {
      // Ignore invalid or unsupported browser locale values.
    }
  }
  return '';
}

function cachedCountry() {
  try {
    const country = window.sessionStorage.getItem(countryStorageKey);
    return country && supportedCountries.has(country.toUpperCase()) ? country.toLowerCase() : '';
  } catch {
    return '';
  }
}

export async function lookupCountryByIp() {
  const cached = cachedCountry();
  if (cached) return cached;

  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 2500);
    try {
      const response = await fetch('/api/v1/public/country', {
        cache: 'no-store',
        headers: {Accept: 'application/json'},
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`geoip_${response.status}`);
      const payload = await response.json();
      const country = typeof payload?.country === 'string' ? payload.country.trim().toUpperCase() : '';
      if (!supportedCountries.has(country)) throw new Error('geoip_invalid_country');
      try { window.sessionStorage.setItem(countryStorageKey, country); } catch { /* Storage is optional. */ }
      return country.toLowerCase();
    } finally {
      window.clearTimeout(timeout);
    }
  } catch {
    return browserTimeZoneCountry() || browserLocaleCountry();
  }
}

/**
 * Mexican formats people still type: "+52 1 …" / "0052 1 …" / "521…" (the mobile "1", dropped in 2019), "044"/"045"
 * and "01". Same rules as the tour registration (src/lib/telefono.ts) and the booking calendar.
 */
export function normalizeMexicanPhone(value, country) {
  const clean = value.trim();
  const digits = clean.replace(/\D/g, '');
  const international = clean.startsWith('+') || clean.startsWith('00');
  const withCode = digits.replace(/^00/, '');
  const mexico = String(country || '').toUpperCase() === 'MX';
  if ((international || mexico) && /^521\d{10}$/.test(withCode)) return `+52${withCode.slice(3)}`;
  if (mexico && !international) {
    if (/^04[45]\d{10}$/.test(digits)) return digits.slice(3);
    if (/^01\d{10}$/.test(digits)) return digits.slice(2);
    // The quiz shows "+52" apart from the number (separateDialCode), so with the Mexican flag "1 81 1234 5678"
    // is the old mobile "1", not a US number (the tour form, without a separate dial code, leaves this case alone).
    if (/^1\d{10}$/.test(digits)) return digits.slice(1);
  }
  return value;
}

export function normalizePhone(value, country) {
  const iso2 = country.trim().toUpperCase();
  if (!supportedCountries.has(iso2)) return null;
  const phone = parsePhoneNumberFromString(normalizeMexicanPhone(value, iso2).trim(), {defaultCountry: iso2, extract: false});
  return phone?.isValid() && !phone.ext && phone.country === iso2 ? phone.number : null;
}

/** "+52 81 1234 5678" for the "Te llamaremos al …" confirmation, or "" when the number is not valid. */
export function formatPhoneForConfirmation(value, country) {
  const number = normalizePhone(value, country);
  return number ? parsePhoneNumberFromString(number)?.formatInternational() ?? '' : '';
}

const countryNames = (() => { try { return new Intl.DisplayNames(['es'], {type: 'region'}); } catch { return null; } })();

/**
 * Why a phone is not valid, as the reason sent to GA4 (vacio, faltan_digitos, sobran_digitos, codigo_pais,
 * no_valido_en_pais) and the message shown to the person. `null` when the number is valid.
 * Same vocabulary and Spanish messages as the tour registration and the booking calendar.
 */
export function phoneProblem(value, country) {
  if (!value.trim()) return {reason: 'vacio', message: 'Escribe tu teléfono para que podamos contactarte.'};
  const iso2 = String(country || '').trim().toUpperCase();
  if (normalizePhone(value, iso2)) return null;
  let length;
  try { length = validatePhoneNumberLength(normalizeMexicanPhone(value, iso2), supportedCountries.has(iso2) ? iso2 : undefined); } catch { length = undefined; }
  const name = supportedCountries.has(iso2) ? countryNames?.of(iso2) : undefined;
  const inCountry = name ? ` en ${name}` : '';
  let digits;
  try { digits = supportedCountries.has(iso2) ? getExampleNumber(iso2, mobileExamples)?.nationalNumber.length : undefined; } catch { digits = undefined; }
  const count = digits ? `${inCountry} son ${digits}` : '';
  if (length === 'TOO_SHORT') return {reason: 'faltan_digitos', message: count ? `A tu número le faltan dígitos:${count}.` : 'A tu número le faltan dígitos.'};
  if (length === 'TOO_LONG') return {reason: 'sobran_digitos', message: count ? `Tu número tiene dígitos de más:${count}.` : 'Tu número tiene dígitos de más.'};
  if (length === 'INVALID_COUNTRY') return {reason: 'codigo_pais', message: 'Revisa el código de país (el número después del +) o elige la bandera.'};
  return {reason: 'no_valido_en_pais', message: `Ese número no es válido${inCountry}. Revísalo o cambia la bandera si es de otro país.`};
}
